/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: getSession(state).isAuthenticated,
    currentLocation: getLocation(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

interface OwnProps extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

export interface AuthenticatedRowStyles {
    LoginHeadWrapper: InjectableCss;
    LinkStyle: InjectableCss;
    linkWrapper: InjectableCss;
    loginWrapper: InjectableCss;
}

export const authenticatedRowStyles: AuthenticatedRowStyles = {
    LoginHeadWrapper: {
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            text-transform: uppercase;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: center;
            justify-content: center;
            padding: 10px;
            margin-bottom: 20px;
            font-size: 28px;
        `,
    },
    LinkStyle: {
        css: css`
            display: inline-block;
            text-align: center;
            cursor: pointer;
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            color: #134078;
            font-size: 18px;
            margin: 3px 0 0;
        `,
    },
    linkWrapper: {
        css: css`
            text-align: center;
        `,
    },
    loginWrapper: {
        css: css`
            margin-bottom: 50px;
        `,
    },
};

const styles = authenticatedRowStyles;

const AuthenticatedRow: React.FunctionComponent<Props> = (props: Props) => {
    useEffect(() => {
        sessionStorage.setItem("redirectURL", window.location.pathname);
    }, []);

    const {
        fields: { columns, gap, alignment, reflow },
        id,
        isAuthenticated,
        currentLocation,
        history,
        signInUrl,
    } = props;

    const handleSignInClick = () => {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        if (returnUrl) {
            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
        }
    };

    if (!isAuthenticated) {
        return (
            <StyledWrapper {...styles.loginWrapper}>
                <StyledWrapper {...styles.LoginHeadWrapper}>
                    {translate("Click On Login Below to see the content")}
                </StyledWrapper>
                <StyledWrapper {...styles.linkWrapper}>
                    <Button onClick={handleSignInClick}>{translate("Login")}</Button>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    return <GridContainer gap={gap || 0}>{columnElements}</GridContainer>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(AuthenticatedRow)),
    definition: {
        group: "Basic",
        icon: "Row",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
